import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { PHeading, PIcon, PInlineNotification, PText } from "@porsche-design-system/components-react"
import type { PHeadingProps, PTextProps } from "@porsche-design-system/components-react"
import {
	breakpoint,
	spacingFluidMd,
	spacingFluidXl,
	spacingStaticSm,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import type { FinancingView } from "@slfinrtl/ui-bff-routes"
import type { PropsWithChildren } from "react"
import { defineMessages, useIntl } from "react-intl"
import { Grid } from "../grid/Grid"
import { GridItem } from "../grid/GridItem"
import { List } from "../list"
import { Tooltip } from "../tooltip/Tooltip"
import type { FinancingNotification } from "./FinancingNotification"
import { FinancingSkeleton } from "./FinancingSkeleton"
import { mapInlineNotificationState } from "./mapInlineNotificationState"

interface StateReady extends FinancingView {
	state: "ready"
}

interface StateNotification extends FinancingNotification {
	state: "notification"
}

interface StateLoading {
	state: "loading"
}

type FinancingProps = StateNotification | StateLoading | StateReady

const messages = defineMessages({
	noProductTypes: {
		defaultMessage: "There are no financial products available for this vehicle",
		description:
			"This message tells the dealer that there are no financial products available for the vehicle for any product type.",
		id: "vehicleDetails.overview.financing.noProductTypes",
	},
})

export const Financing = (props: FinancingProps) => {
	const { state } = props
	const { formatMessage } = useIntl()

	if (state === "notification") {
		const { severity, text, title } = props
		return (
			<PInlineNotification
				state={mapInlineNotificationState(severity)}
				heading={title}
				description={text}
				dismissButton={false}
			/>
		)
	}

	if (state === "loading") {
		return <FinancingSkeleton />
	}

	if (state !== "ready") {
		return null
	}

	const { productTypes } = props
	if (productTypes.length <= 0) {
		return (
			<PInlineNotification
				state="info"
				description={formatMessage(messages.noProductTypes)}
				dismissButton={false}
			/>
		)
	}

	return (
		<Grid $columnGap={{ base: spacingFluidMd, s: spacingFluidXl }} $rowGap={spacingFluidMd}>
			{productTypes.map(({ name, product }, i, arr) => {
				// in case 1st item is disabled it is quite small and produces lots of white space to 3rd item.
				// to fix this, we stretch 2nd item to double height
				// the check for arr.length === 3 is for safety in case the amount of productTypes decreases/increases
				const isSecondOfThree = i === 1 && arr.length === 3

				const header = (
					<>
						<ProductType>{name}</ProductType>
						{typeof product === "object" && (
							<PText>
								{product.name}
								{product.description && (
									<Tooltip
										// We use the Text component since PText
										// is rendered asynchronously and might render the tooltip slightly wrong
										content={product.description}
									>
										<ProductDescriptionIcon />
									</Tooltip>
								)}
							</PText>
						)}
					</>
				)

				return typeof product === "string" ? (
					<StyledGridItem key={name} size={{ base: 12, m: 4 }} $stretchRows={isSecondOfThree}>
						{header}
						<PText color="contrast-medium">{product}</PText>
					</StyledGridItem>
				) : (
					<StyledGridItem key={name} size={{ base: 12, m: 4 }} $stretchRows={isSecondOfThree}>
						{product.summary ? (
							<>
								<List header={header}>
									{product.summary.items.map((item) => (
										<List.Item key={item.label} {...item} />
									))}
								</List>
								{product.summary.hint && <ProductTaxInfo>{product.summary.hint}</ProductTaxInfo>}
							</>
						) : (
							header
						)}
					</StyledGridItem>
				)
			})}
		</Grid>
	)
}

const StyledGridItem = styled(GridItem)<{ $stretchRows?: boolean }>`
	${({ $stretchRows }) => $stretchRows && css`@media (min-width: ${breakpoint.m}px) { grid-row: span 2; }`}
`

const ProductType = styled(({ children, ...rest }: PropsWithChildren<PHeadingProps>) => (
	<PHeading size="md" {...rest}>
		{children}
	</PHeading>
))`
	margin-bottom: ${spacingStaticXs};
`

const ProductDescriptionIcon = styled(() => <PIcon name="information" />)`
	margin-inline-start: ${spacingStaticXs};
`

const ProductTaxInfo = styled(({ children, ...rest }: PropsWithChildren<PTextProps>) => (
	<PText align="end" color="contrast-medium" size="xs" {...rest}>
		{children}
	</PText>
))`
	margin: ${spacingStaticSm} 0 0
`
